import React, { useEffect } from 'react';
import SEO from '../components/SEO';
import './TermsAndConditions.css';

const TermsAndConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="terms-container">
      <SEO 
        title="Terms & Conditions - BlackKiteAI"
        description="Terms and conditions for using BlackKiteAI's AI-powered interview assistant service."
        canonical="https://www.blackkiteai.com/terms-and-conditions"
      />
      <div className="terms-content">
        <h1>Terms & Conditions</h1>
        <p className="last-updated">Last Updated: January 5, 2026</p>

        <section>
          <p>
            Welcome to BlackKiteAI. These Terms and Conditions ("Terms") govern your access to 
            and use of BlackKiteAI's AI-powered risk scoring, threat intelligence, and security 
            analytics platform ("Service"). By accessing or using our Service, you agree to be 
            bound by these Terms. If you do not agree to these Terms, please do not use our Service.
          </p>
        </section>

        <section>
          <h2>1. Definitions</h2>
          <ul>
            <li><strong>"Service"</strong> refers to the BlackKiteAI platform, including all software, applications, APIs, and related services.</li>
            <li><strong>"User," "You," or "Your"</strong> refers to any individual or entity accessing or using the Service.</li>
            <li><strong>"Account"</strong> refers to the user account created to access the Service.</li>
            <li><strong>"Credits"</strong> refers to the prepaid usage units required to access certain features of the Service.</li>
            <li><strong>"Content"</strong> refers to any data, information, text, files, or materials uploaded, submitted, or processed through the Service.</li>
            <li><strong>"We," "Us," or "Our"</strong> refers to BlackKiteAI and its affiliates.</li>
          </ul>
        </section>

        <section>
          <h2>2. Acceptance of Terms</h2>
          <p>
            By creating an account, accessing, or using the Service, you acknowledge that you have 
            read, understood, and agree to be bound by these Terms, as well as our Privacy Policy 
            and Refund Policy. These Terms constitute a legally binding agreement between you and 
            BlackKiteAI.
          </p>
          <p>
            If you are using the Service on behalf of an organization, you represent and warrant 
            that you have the authority to bind that organization to these Terms, and your agreement 
            to these Terms will be treated as the agreement of that organization.
          </p>
        </section>

        <section>
          <h2>3. Eligibility</h2>
          <p>
            You must be at least 18 years of age to use our Service. By using the Service, you 
            represent and warrant that:
          </p>
          <ul>
            <li>You are at least 18 years old and have the legal capacity to enter into a binding contract</li>
            <li>You are not prohibited from using the Service under applicable laws</li>
            <li>Your use of the Service will not violate any laws or regulations applicable to you</li>
            <li>All information you provide to us is accurate, current, and complete</li>
          </ul>
        </section>

        <section>
          <h2>4. Account Registration and Security</h2>
          
          <h3>4.1 Account Creation</h3>
          <p>
            To access certain features of the Service, you must create an account by providing 
            accurate and complete information, including a valid email address and password. You 
            are responsible for maintaining the confidentiality of your account credentials.
          </p>

          <h3>4.2 Account Security</h3>
          <p>You agree to:</p>
          <ul>
            <li>Keep your password secure and confidential</li>
            <li>Notify us immediately of any unauthorized access or security breach</li>
            <li>Accept responsibility for all activities that occur under your account</li>
            <li>Not share your account credentials with any third party</li>
            <li>Not create multiple accounts to circumvent usage limitations or billing requirements</li>
          </ul>

          <h3>4.3 Account Termination</h3>
          <p>
            We reserve the right to suspend or terminate your account at any time, with or without 
            notice, for any reason, including but not limited to violation of these Terms, fraudulent 
            activity, or misuse of the Service.
          </p>
        </section>

        <section>
          <h2>5. Service Description</h2>
          <p>
            BlackKiteAI provides an AI-powered platform for:
          </p>
          <ul>
            <li>Risk scoring and assessment of third-party vendors and organizations</li>
            <li>Threat intelligence gathering and analysis</li>
            <li>Security analytics and vulnerability detection</li>
            <li>Cybersecurity posture evaluation</li>
            <li>Compliance monitoring and reporting</li>
            <li>Interview preparation and practice sessions powered by AI</li>
          </ul>
          <p>
            We reserve the right to modify, suspend, or discontinue any aspect of the Service at 
            any time, with or without notice, and without liability to you.
          </p>
        </section>

        <section>
          <h2>6. Credits and Payment</h2>
          
          <h3>6.1 Credit System</h3>
          <p>
            Our Service operates on a prepaid credit-based system. Credits are required to access 
            certain features and must be purchased in advance. Credit packages and pricing are 
            displayed on our platform and are subject to change at our discretion.
          </p>

          <h3>6.2 Payment Terms</h3>
          <ul>
            <li>All purchases are processed through secure third-party payment providers</li>
            <li>Payments are non-refundable except as specified in our Refund Policy</li>
            <li>You are responsible for all applicable taxes, fees, and charges</li>
            <li>We reserve the right to modify pricing at any time</li>
            <li>Promotional pricing may be subject to additional terms and conditions</li>
          </ul>

          <h3>6.3 Credit Expiration</h3>
          <p>
            Purchased credits do not expire unless explicitly stated at the time of purchase or 
            unless your account is terminated for violation of these Terms.
          </p>

          <h3>6.4 Billing Disputes</h3>
          <p>
            If you believe you have been incorrectly charged, you must contact us within 30 days 
            of the transaction date. Failure to notify us within this period may result in your 
            waiver of any claim relating to such charges.
          </p>
        </section>

        <section>
          <h2>7. Acceptable Use Policy</h2>
          <p>You agree not to use the Service to:</p>
          <ul>
            <li>Violate any applicable laws, regulations, or third-party rights</li>
            <li>Transmit any unlawful, harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable content</li>
            <li>Impersonate any person or entity or falsely represent your affiliation with any person or entity</li>
            <li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
            <li>Attempt to gain unauthorized access to any portion of the Service or any other systems or networks</li>
            <li>Use automated means (including bots, scrapers, or scripts) to access the Service without our express written permission</li>
            <li>Reverse engineer, decompile, disassemble, or otherwise attempt to discover the source code of the Service</li>
            <li>Remove, obscure, or alter any proprietary rights notices on the Service</li>
            <li>Use the Service to transmit any viruses, malware, or other malicious code</li>
            <li>Engage in any activity that could damage, disable, overburden, or impair the Service</li>
            <li>Use the Service for any competitive analysis or to develop competing products</li>
          </ul>
        </section>

        <section>
          <h2>8. Intellectual Property Rights</h2>
          
          <h3>8.1 Our Intellectual Property</h3>
          <p>
            The Service, including all software, algorithms, designs, trademarks, logos, and content, 
            is owned by BlackKiteAI and is protected by intellectual property laws. We grant you a 
            limited, non-exclusive, non-transferable, revocable license to access and use the Service 
            for its intended purpose, subject to these Terms.
          </p>

          <h3>8.2 Your Content</h3>
          <p>
            You retain all ownership rights to the content you upload or submit to the Service. By 
            uploading or submitting content, you grant us a worldwide, non-exclusive, royalty-free 
            license to use, process, store, and analyze your content solely for the purpose of 
            providing and improving the Service.
          </p>

          <h3>8.3 Feedback</h3>
          <p>
            If you provide us with any feedback, suggestions, or ideas regarding the Service, you 
            grant us an unrestricted, perpetual, irrevocable, royalty-free license to use, modify, 
            and incorporate such feedback into the Service without any obligation to you.
          </p>
        </section>

        <section>
          <h2>9. Data Privacy and Security</h2>
          <p>
            We take data privacy and security seriously. Our collection, use, and protection of your 
            personal data is governed by our Privacy Policy, which is incorporated into these Terms 
            by reference. By using the Service, you consent to our data practices as described in the 
            Privacy Policy.
          </p>
          <p>
            While we implement industry-standard security measures, no method of transmission or 
            storage is 100% secure. You acknowledge that you provide your information at your own risk.
          </p>
        </section>

        <section>
          <h2>10. Service Availability and Modifications</h2>
          
          <h3>10.1 Service Availability</h3>
          <p>
            We strive to provide continuous availability of the Service but do not guarantee 
            uninterrupted access. The Service may be temporarily unavailable due to maintenance, 
            updates, technical issues, or circumstances beyond our control.
          </p>

          <h3>10.2 Modifications to Service</h3>
          <p>
            We reserve the right to modify, enhance, or discontinue any features or functionality 
            of the Service at any time without prior notice. We are not liable for any modifications, 
            suspension, or discontinuation of the Service.
          </p>

          <h3>10.3 Updates to Terms</h3>
          <p>
            We may update these Terms from time to time. When we make material changes, we will 
            update the "Last Updated" date and notify you through the Service or via email. Your 
            continued use of the Service after such changes constitutes acceptance of the updated Terms.
          </p>
        </section>

        <section>
          <h2>11. Third-Party Services and Links</h2>
          <p>
            The Service may contain links to third-party websites, services, or resources that are 
            not owned or controlled by BlackKiteAI. We are not responsible for the content, privacy 
            policies, or practices of any third-party websites or services. You acknowledge and agree 
            that we shall not be liable for any damage or loss caused by your use of any third-party 
            services.
          </p>
        </section>

        <section>
          <h2>12. Disclaimers and Limitations of Liability</h2>
          
          <h3>12.1 No Warranty</h3>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
            EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF 
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT 
            WARRANT THAT THE SERVICE WILL BE ERROR-FREE, SECURE, OR UNINTERRUPTED.
          </p>

          <h3>12.2 Limitation of Liability</h3>
          <p>
            TO THE FULLEST EXTENT PERMITTED BY LAW, BLACKKITEAI SHALL NOT BE LIABLE FOR ANY 
            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF 
            PROFITS, REVENUE, DATA, OR USE, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE, 
            EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </p>
          <p>
            IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS RELATED TO THE SERVICE 
            EXCEED THE AMOUNT YOU PAID TO US IN THE SIX (6) MONTHS PRECEDING THE EVENT GIVING 
            RISE TO LIABILITY, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS GREATER.
          </p>

          <h3>12.3 AI-Generated Content Disclaimer</h3>
          <p>
            The Service utilizes artificial intelligence and machine learning technologies to 
            generate risk assessments, threat intelligence, and security analytics. While we strive 
            for accuracy, AI-generated content may contain errors, inaccuracies, or outdated 
            information. You are solely responsible for verifying the accuracy of any information 
            obtained through the Service and for any decisions made based on such information.
          </p>
          <p>
            BlackKiteAI does not provide legal, financial, or professional advice. The Service is 
            intended for informational purposes only and should not be relied upon as a substitute 
            for professional consultation.
          </p>
        </section>

        <section>
          <h2>13. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless BlackKiteAI, its affiliates, officers, 
            directors, employees, agents, and licensors from and against any claims, liabilities, 
            damages, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising 
            from:
          </p>
          <ul>
            <li>Your use or misuse of the Service</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any rights of any third party</li>
            <li>Your content or any content submitted through your account</li>
          </ul>
        </section>

        <section>
          <h2>14. Governing Law and Dispute Resolution</h2>
          
          <h3>14.1 Governing Law</h3>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the 
            jurisdiction in which BlackKiteAI is registered, without regard to its conflict of 
            law provisions.
          </p>

          <h3>14.2 Dispute Resolution</h3>
          <p>
            Any dispute arising out of or relating to these Terms or the Service shall be resolved 
            through good-faith negotiations. If the dispute cannot be resolved through negotiations 
            within thirty (30) days, either party may pursue resolution through binding arbitration 
            or litigation in the courts of competent jurisdiction.
          </p>

          <h3>14.3 Class Action Waiver</h3>
          <p>
            You agree to bring any dispute in an individual capacity and not as a plaintiff or 
            class member in any purported class, representative, or collective action. You waive 
            your right to participate in a class action lawsuit or class-wide arbitration.
          </p>
        </section>

        <section>
          <h2>15. Export Compliance</h2>
          <p>
            The Service may be subject to export control laws and regulations. You agree to comply 
            with all applicable export and import laws and regulations and will not export, re-export, 
            or transfer the Service to prohibited countries, entities, or individuals.
          </p>
        </section>

        <section>
          <h2>16. Severability</h2>
          <p>
            If any provision of these Terms is found to be invalid, illegal, or unenforceable, the 
            remaining provisions shall continue in full force and effect. The invalid or unenforceable 
            provision shall be replaced with a valid and enforceable provision that most closely 
            reflects the intent of the original provision.
          </p>
        </section>

        <section>
          <h2>17. Entire Agreement</h2>
          <p>
            These Terms, together with our Privacy Policy and Refund Policy, constitute the entire 
            agreement between you and BlackKiteAI regarding the Service and supersede all prior 
            agreements, understandings, and communications, whether written or oral.
          </p>
        </section>

        <section>
          <h2>18. Assignment</h2>
          <p>
            You may not assign or transfer these Terms or your rights under these Terms without 
            our prior written consent. We may assign or transfer these Terms at any time without 
            restriction.
          </p>
        </section>

        <section>
          <h2>19. Waiver</h2>
          <p>
            Our failure to enforce any right or provision of these Terms shall not constitute a 
            waiver of such right or provision. Any waiver must be in writing and signed by an 
            authorized representative of BlackKiteAI.
          </p>
        </section>

        <section>
          <h2>20. Contact Information</h2>
          <p>
            If you have any questions, concerns, or complaints regarding these Terms or the Service, 
            please contact us at:
          </p>
          <div className="contact-info">
            <p><strong>BlackKiteAI Support</strong></p>
            <p>Email: support@blackkiteai.com</p>
            <p>Response Time: Within 48 hours</p>
          </div>
        </section>

        <section className="acknowledgment">
          <h2>Acknowledgment</h2>
          <p>
            BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE 
            BOUND BY THESE TERMS AND CONDITIONS.
          </p>
        </section>
      </div>
    </div>
  );
};

export default TermsAndConditions;
